import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  Calendar,
  Users,
  Wallet,
  Compass,
  Sparkles,
  Plus,
  Search,
  Share2,
  Trash2,
  Edit3,
  ArrowRight,
  Loader2,
  FileText,
  Clock,
  CheckCircle2,
  GitPullRequest,
  Copy,
  Check
} from 'lucide-react';
import tripsApi from '../api/tripsApi';
import SocialShareModal from './SocialShareModal';
import { useAuth } from '../context/AuthContext';

export default function MyTripsView() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]);
  const [shared, setShared] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'drafts' | 'shared'
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [shareTrip, setShareTrip] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchTrips = async () => {
      try {
        setLoading(true);
        const data = await tripsApi.getDashboard();
        if (isMounted && data) {
          setDrafts(data.drafts || []);
          setShared(data.shared || []);
        }
      } catch (err) {
        console.error('Failed to load trips for MyTripsView:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchTrips();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleDeleteTrip = async (e, trip, isDraft) => {
    e.stopPropagation();
    const confirmed = window.confirm(`Are you sure you want to delete "${trip.title || trip.destination}"?`);
    if (!confirmed) return;

    try {
      setDeletingId(trip.id);
      if (isDraft) {
        await tripsApi.deleteDraft(trip.id);
        setDrafts(prev => prev.filter(t => t.id !== trip.id));
      } else {
        await tripsApi.deleteSharedTrip(trip.id);
        setShared(prev => prev.filter(t => t.id !== trip.id));
      }
    } catch (err) {
      console.error('Failed to delete trip:', err);
      alert(`Could not delete trip: ${err.message || 'Unknown error'}`);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyLink = (e, trip) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/dashboard/trip/${trip.id}`;
    navigator.clipboard.writeText(shareUrl);
    setCopiedId(trip.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filtered trips
  const filteredDrafts = useMemo(() => {
    if (!searchQuery.trim()) return drafts;
    const q = searchQuery.toLowerCase();
    return drafts.filter(
      d =>
        d.title?.toLowerCase().includes(q) ||
        d.destination?.toLowerCase().includes(q) ||
        d.origin?.toLowerCase().includes(q)
    );
  }, [drafts, searchQuery]);

  const filteredShared = useMemo(() => {
    if (!searchQuery.trim()) return shared;
    const q = searchQuery.toLowerCase();
    return shared.filter(
      s =>
        s.title?.toLowerCase().includes(q) ||
        s.destination?.toLowerCase().includes(q) ||
        s.origin?.toLowerCase().includes(q)
    );
  }, [shared, searchQuery]);

  const totalCount = drafts.length + shared.length;

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 sm:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200/60 text-[#2A9D8F] text-xs font-bold uppercase tracking-wider mb-2">
            <Compass className="w-3.5 h-3.5" />
            <span>Itinerary Center</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">
            My Trips &amp; Expeditions
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Manage your AI blueprints, active shared itineraries, and companion travel groups.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard/ai-planner')}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white text-xs font-bold shadow-sm transition-all duration-200 cursor-pointer"
          >
            <Sparkles className="w-4 h-4" />
            <span>AI Trip Planner</span>
          </button>
          <button
            onClick={() => navigate('/dashboard/create-trip')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-gradient-to-r from-[#2A9D8F] to-[#238177] hover:from-[#238177] hover:to-[#1a6860] text-white text-xs font-bold shadow-sm transition-all duration-200 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Create Trip</span>
          </button>
        </div>
      </div>

      {/* ── Search & Filter Controls ── */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Segmented Tabs */}
        <div className="flex items-center bg-gray-100/80 p-1.5 rounded-2xl w-full sm:w-auto">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === 'all'
                ? 'bg-white text-gray-900 shadow-xs'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <span>All Trips</span>
            <span className="px-2 py-0.5 rounded-full bg-gray-200/60 text-[10px]">
              {totalCount}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('shared')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === 'shared'
                ? 'bg-white text-gray-900 shadow-xs'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <span>Live Expeditions</span>
            <span className="px-2 py-0.5 rounded-full bg-teal-100 text-[#2A9D8F] text-[10px]">
              {shared.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('drafts')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === 'drafts'
                ? 'bg-white text-gray-900 shadow-xs'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <span>Drafts</span>
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px]">
              {drafts.length}
            </span>
          </button>
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by destination or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-white border border-gray-200 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]/30 focus:border-[#2A9D8F] transition-all"
          />
        </div>
      </div>

      {/* ── Content Grid ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-xs space-y-3">
          <Loader2 className="w-8 h-8 text-[#2A9D8F] animate-spin" />
          <span className="text-xs font-bold text-gray-500">Loading your journeys...</span>
        </div>
      ) : totalCount === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-xs space-y-4 max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-3xl bg-teal-50 text-[#2A9D8F] flex items-center justify-center mx-auto">
            <Compass className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">No Trips Yet</h3>
          <p className="text-xs text-gray-500 leading-relaxed">
            You haven't planned any trips yet. Generate a hyper-realistic AI itinerary in 10 seconds or craft your own custom road trip!
          </p>
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              onClick={() => navigate('/dashboard/ai-planner')}
              className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-[#2A9D8F] to-[#238177] text-white text-xs font-bold shadow-sm"
            >
              Plan with AI
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* ── Live / Shared Trips Section ── */}
          {(activeTab === 'all' || activeTab === 'shared') && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-black text-gray-900 tracking-tight flex items-center gap-2">
                  <Compass className="w-4 h-4 text-[#2A9D8F]" />
                  <span>Live &amp; Shared Expeditions ({filteredShared.length})</span>
                </h2>
              </div>

              {filteredShared.length === 0 ? (
                <div className="p-8 bg-white rounded-3xl border border-gray-100 text-center text-xs text-gray-400">
                  {searchQuery ? 'No shared expeditions matched your search query.' : 'No published expeditions found.'}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredShared.map((trip) => {
                    const cover =
                      trip.coverImageUrl && trip.coverImageUrl !== 'Not Applicable'
                        ? trip.coverImageUrl
                        : 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800';
                    const daysCount = trip.totalDays || (trip.days ? trip.days.length : 1);
                    const isOwner = trip.userId === user?.id;

                    return (
                      <div
                        key={trip.id}
                        onClick={() => navigate(`/dashboard/trip/${trip.id}`)}
                        className="group bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col justify-between cursor-pointer hover:-translate-y-1"
                      >
                        <div>
                          {/* Image Cover */}
                          <div className="relative h-48 w-full overflow-hidden bg-slate-900">
                            <img
                              src={cover}
                              alt={trip.destination}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800';
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

                            {/* Status Badge */}
                            <div className="absolute top-3 left-3">
                              <span className="px-3 py-1 rounded-full bg-emerald-500/90 backdrop-blur-md text-white text-[11px] font-extrabold tracking-wide uppercase shadow-sm">
                                Live Trip
                              </span>
                            </div>

                            {/* Trip Actions */}
                            <div className="absolute top-3 right-3 flex items-center gap-1.5">
                              <button
                                onClick={(e) => handleCopyLink(e, trip)}
                                title="Copy Share Link"
                                className="w-8 h-8 rounded-full bg-black/50 hover:bg-black/80 text-white backdrop-blur-md flex items-center justify-center transition-colors cursor-pointer"
                              >
                                {copiedId === trip.id ? (
                                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShareTrip(trip);
                                }}
                                title="Share Cards"
                                className="w-8 h-8 rounded-full bg-black/50 hover:bg-black/80 text-white backdrop-blur-md flex items-center justify-center transition-colors cursor-pointer"
                              >
                                <Share2 className="w-3.5 h-3.5" />
                              </button>
                              {isOwner && (
                                <button
                                  onClick={(e) => handleDeleteTrip(e, trip, false)}
                                  title="Delete Trip"
                                  disabled={deletingId === trip.id}
                                  className="w-8 h-8 rounded-full bg-rose-600/70 hover:bg-rose-600 text-white backdrop-blur-md flex items-center justify-center transition-colors cursor-pointer"
                                >
                                  {deletingId === trip.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              )}
                            </div>

                            {/* Destination Title */}
                            <div className="absolute bottom-3 left-3 right-3 text-white">
                              <h3 className="text-lg font-black leading-tight drop-shadow-md truncate">
                                {trip.title || trip.destination}
                              </h3>
                              <p className="text-xs text-slate-200 flex items-center gap-1 mt-0.5 truncate">
                                <MapPin className="w-3 h-3 text-teal-400 shrink-0" />
                                <span>{trip.destination}</span>
                                {trip.origin && <span className="opacity-75">· From {trip.origin}</span>}
                              </p>
                            </div>
                          </div>

                          {/* Details Bar */}
                          <div className="p-5 space-y-3 text-xs text-gray-600">
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-[#2A9D8F]" />
                                <span className="font-semibold">{daysCount} Days</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="w-3.5 h-3.5 text-[#2A9D8F]" />
                                <span className="font-semibold">{trip.travelers || 2} Travelers</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                              <span className="text-gray-400 font-medium">Target Budget</span>
                              <span className="font-extrabold text-emerald-600 font-mono text-sm">
                                ₹{Number(trip.budget || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Card Footer Actions */}
                        <div className="px-5 pb-5 pt-0 flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/create-trip?sharedTripId=${trip.id}`);
                            }}
                            className="flex-1 py-2 px-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-bold border border-gray-200 transition-colors flex items-center justify-center gap-1.5"
                          >
                            <Edit3 className="w-3.5 h-3.5 text-gray-500" />
                            <span>Edit Trip</span>
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/trip/${trip.id}/change-requests`);
                            }}
                            className="py-2 px-3 rounded-xl bg-teal-50 hover:bg-teal-100 text-teal-800 text-xs font-bold border border-teal-200 transition-colors flex items-center gap-1"
                            title="Review PR Changes"
                          >
                            <GitPullRequest className="w-3.5 h-3.5 text-[#2A9D8F]" />
                            <span>PRs</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Draft Trips Section ── */}
          {(activeTab === 'all' || activeTab === 'drafts') && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-black text-gray-900 tracking-tight flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-500" />
                  <span>Draft Itineraries ({filteredDrafts.length})</span>
                </h2>
              </div>

              {filteredDrafts.length === 0 ? (
                <div className="p-8 bg-white rounded-3xl border border-gray-100 text-center text-xs text-gray-400">
                  {searchQuery ? 'No draft itineraries matched your search query.' : 'No drafts in progress.'}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredDrafts.map((draft) => {
                    const cover =
                      draft.coverImageUrl && draft.coverImageUrl !== 'Not Applicable'
                        ? draft.coverImageUrl
                        : 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800';
                    const daysCount = draft.totalDays || (draft.days ? draft.days.length : 1);

                    return (
                      <div
                        key={draft.id}
                        onClick={() => navigate(`/dashboard/create-trip?draftId=${draft.id}`)}
                        className="group bg-white rounded-3xl border border-dashed border-gray-300 overflow-hidden shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col justify-between cursor-pointer hover:-translate-y-1 hover:border-[#2A9D8F]"
                      >
                        <div>
                          {/* Image Cover */}
                          <div className="relative h-44 w-full overflow-hidden bg-slate-900">
                            <img
                              src={cover}
                              alt={draft.destination}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800';
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

                            {/* Status Badge */}
                            <div className="absolute top-3 left-3">
                              <span className="px-3 py-1 rounded-full bg-amber-500/90 backdrop-blur-md text-white text-[11px] font-extrabold tracking-wide uppercase shadow-sm flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <span>Draft</span>
                              </span>
                            </div>

                            {/* Delete Draft Button */}
                            <div className="absolute top-3 right-3">
                              <button
                                onClick={(e) => handleDeleteTrip(e, draft, true)}
                                title="Delete Draft"
                                disabled={deletingId === draft.id}
                                className="w-8 h-8 rounded-full bg-rose-600/70 hover:bg-rose-600 text-white backdrop-blur-md flex items-center justify-center transition-colors cursor-pointer"
                              >
                                {deletingId === draft.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>

                            {/* Destination Title */}
                            <div className="absolute bottom-3 left-3 right-3 text-white">
                              <h3 className="text-lg font-black leading-tight drop-shadow-md truncate">
                                {draft.title || draft.destination}
                              </h3>
                              <p className="text-xs text-slate-200 flex items-center gap-1 mt-0.5 truncate">
                                <MapPin className="w-3 h-3 text-amber-400 shrink-0" />
                                <span>{draft.destination}</span>
                                {draft.origin && <span className="opacity-75">· From {draft.origin}</span>}
                              </p>
                            </div>
                          </div>

                          {/* Details Bar */}
                          <div className="p-5 space-y-3 text-xs text-gray-600">
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-amber-500" />
                                <span className="font-semibold">{daysCount} Days</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="w-3.5 h-3.5 text-amber-500" />
                                <span className="font-semibold">{draft.travelers || 1} Traveler</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                              <span className="text-gray-400 font-medium">Estimated Budget</span>
                              <span className="font-extrabold text-gray-900 font-mono text-sm">
                                ₹{Number(draft.budget || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Continue Button */}
                        <div className="px-5 pb-5 pt-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/create-trip?draftId=${draft.id}`);
                            }}
                            className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-xs font-bold shadow-xs transition-all flex items-center justify-center gap-2"
                          >
                            <span>Continue Crafting</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Social Share Modal */}
      {shareTrip && (
        <SocialShareModal
          isOpen={Boolean(shareTrip)}
          onClose={() => setShareTrip(null)}
          trip={shareTrip}
          organizer={user}
        />
      )}
    </div>
  );
}
